import { graphql, useStaticQuery } from 'gatsby';
import { useHasMounted } from 'hem-hooks';
import isArray from 'lodash/isArray';
import toNumber from 'lodash/toNumber';
import React, { useEffect, useState } from 'react';

import { useCustomer } from '@/hooks/useCustomer';
import { useShopifyStore } from '@/hooks/useShopifyStore';

import { IS_PRO, RegionEnum } from '../constants';

export const getIso639CodeAndCurrency = (): {
  iso639Code: string;
  currencyCode: string;
} => {
  switch (process.env.GATSBY_REGION) {
    case RegionEnum.Ca:
      return { iso639Code: 'en-CA', currencyCode: 'CAD' };
    case RegionEnum.Eu:
      return { iso639Code: 'sv-SE', currencyCode: 'EUR' };
    case RegionEnum.Gb:
      return { iso639Code: 'en-GB', currencyCode: 'GBP' };
    case RegionEnum.Us:
    default:
      return { iso639Code: 'en-US', currencyCode: 'USD' };
  }
};

export const Money: React.FC<{
  amount: number;
  withDiscount?: boolean;
  numberFormatStyle?: 'decimal' | 'currency' | 'percent' | 'unit';
  sku?: string;
}> = ({
  amount,
  withDiscount = true,
  numberFormatStyle = 'currency',
  sku,
}): JSX.Element => {
    const { campaign, excludes } = useStaticQuery<Queries.CampaignDiscountsQuery>(
      graphql`
      query CampaignDiscounts {
        campaign: contentfulCampaign(fields: { activeCampaign: { eq: true } }) {
          discounts {
            tieredDiscounts {
              aboveAmount
              percentageMultiplier
            }
          }
        }
        excludes: allShopifyProductVariant(
          filter: { product: { tags: { in: "pro-week-exclude" } } }
        ) {
          nodes {
            sku
          }
        }
      }
    `,
    );

    const hasMounted = useHasMounted();
    const { customer, setCurrentProDiscount } = useCustomer();
    const { cart } = useShopifyStore();
    const [proSubtotal, setProSubtotal] = useState(0);
    const [proDiscount, setProDiscount] = useState(1); // 1 = no discount, 0.75 = 25% discount, etc

    useEffect(() => {
      if (!hasMounted) return;
      setProSubtotal(toNumber(cart?.cost?.subtotalAmount?.amount ?? 0));
    }, [hasMounted, proDiscount, cart?.cost?.subtotalAmount?.amount]);

    useEffect(() => {
      if (!hasMounted) return;

      if (customer && IS_PRO) {
        let discount = 0.75;

        // then check for customer discounts
        const customerDiscountTag = customer.tags.find((tag) =>
          tag.startsWith('discount'),
        );

        if (customerDiscountTag) {
          const customerTagPercentageDiscount = toNumber(
            customerDiscountTag.split('-')[1],
          );
          const customerTagDiscount = 1 - customerTagPercentageDiscount / 100;
          if (customerTagDiscount < discount) {
            discount = customerTagDiscount;
          }
        }

        const shouldByExcludedFromCampaign = excludes.nodes.some(
          (excluded) => excluded.sku === sku,
        );

        // If there's a campaign set, check for campaign discounts and
        // compare against default/tag discount and then apply the highest one
        if (
          isArray(campaign?.discounts?.tieredDiscounts) &&
          !shouldByExcludedFromCampaign
        ) {
          // sort tieredDiscounts by aboveAmount, highest number first,
          // so that we can apply the highest discount with .find (returns first match)
          const sortedDiscounts = (
            campaign.discounts.tieredDiscounts as unknown as {
              aboveAmount: number;
              percentageMultiplier: number;
            }[]
          ).sort((a, b) => b.aboveAmount - a.aboveAmount);

          const multiplier = sortedDiscounts.find(
            (disc) => disc.aboveAmount <= discount,
          )?.percentageMultiplier;

          setProDiscount(multiplier);
          // turn 0.75 into 25%, 0.8 to 20%, etc
          setCurrentProDiscount(`${100 - multiplier * 100}%`);
        } else {
          setProDiscount(discount);
          // turn 0.75 into 25%, 0.8 to 20%, etc
          setCurrentProDiscount(`${100 - discount * 100}%`);
        }
      }
    }, [
      hasMounted,
      customer,
      setCurrentProDiscount,
      proSubtotal,
      campaign,
      excludes.nodes,
      sku,
    ]);

    const amountWithDiscount = withDiscount ? toNumber(amount) * proDiscount : toNumber(amount);

    const { iso639Code, currencyCode } = getIso639CodeAndCurrency();
    const money = new Intl.NumberFormat(iso639Code, {
      style: numberFormatStyle,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currency: currencyCode,
    })
      .format(amountWithDiscount)
      .replace(/\D00(?=\D*$)/, '');

    return <>{money}</>;
  };

export const useMoneyString = ({
  amount,
  withDiscount = true,
  numberFormatStyle = 'currency',
  sku,
}: {
  amount: number;
  withDiscount?: boolean;
  numberFormatStyle?: 'decimal' | 'currency' | 'percent' | 'unit';
  sku?: string;
}): string => {
  const { campaign, excludes } = useStaticQuery<Queries.CampaignDiscountsQuery>(
    graphql`
      query CampaignDiscounts {
        campaign: contentfulCampaign(fields: { activeCampaign: { eq: true } }) {
          discounts {
            tieredDiscounts {
              aboveAmount
              percentageMultiplier
            }
          }
        }
        excludes: allShopifyProductVariant(
          filter: { product: { tags: { in: "pro-week-exclude" } } }
        ) {
          nodes {
            sku
          }
        }
      }
    `,
  );

  const hasMounted = useHasMounted();
  const { customer, setCurrentProDiscount } = useCustomer();
  const { cart } = useShopifyStore();
  const [proSubtotal, setProSubtotal] = useState(0);
  const [proDiscount, setProDiscount] = useState(1); // 1 = no discount, 0.75 = 25% discount, etc

  useEffect(() => {
    if (!hasMounted) return;
    setProSubtotal(toNumber(cart?.cost?.subtotalAmount?.amount ?? 0));
  }, [hasMounted, proDiscount, cart?.cost?.subtotalAmount?.amount]);

  useEffect(() => {
    if (!hasMounted) return;

    if (customer && IS_PRO) {
      let discount = 0.75;

      // then check for customer discounts
      const customerDiscountTag = customer.tags.find((tag) =>
        tag.startsWith('discount'),
      );

      if (customerDiscountTag) {
        // prettier-ignore
        const customerTagPercentageDiscount = toNumber(customerDiscountTag.split('-')[1]);
        const customerTagDiscount = 1 - customerTagPercentageDiscount / 100;
        if (customerTagDiscount < discount) {
          discount = customerTagDiscount;
        }
      }

      const shouldByExcludedFromCampaign = excludes.nodes.some(
        (excluded) => excluded.sku === sku,
      );

      // If there's a campaign set, check for campaign discounts and
      // compare against default/tag discount and then apply the highest one
      if (
        isArray(campaign?.discounts?.tieredDiscounts) &&
        !shouldByExcludedFromCampaign
      ) {
        // sort tieredDiscounts by aboveAmount, highest number first,
        // so that we can apply the highest discount with .find (returns first match)
        const sortedDiscounts = (
          campaign.discounts.tieredDiscounts as unknown as {
            aboveAmount: number;
            percentageMultiplier: number;
          }[]
        ).sort((a, b) => b.aboveAmount - a.aboveAmount);

        const multiplier = sortedDiscounts.find(
          (disc) => disc.aboveAmount <= discount,
        )?.percentageMultiplier;

        setProDiscount(multiplier);
        // turn 0.75 into 25%, 0.8 to 20%, etc
        setCurrentProDiscount(`${100 - multiplier * 100}%`);
      } else {
        setProDiscount(discount);
        // turn 0.75 into 25%, 0.8 to 20%, etc
        setCurrentProDiscount(`${100 - discount * 100}%`);
      }
    }
  }, [
    hasMounted,
    customer,
    setCurrentProDiscount,
    proSubtotal,
    campaign,
    excludes.nodes,
    sku,
  ]);

  const amountWithDiscount = withDiscount
    ? toNumber(amount) * proDiscount
    : toNumber(amount);

  const { iso639Code, currencyCode } = getIso639CodeAndCurrency();
  const money = new Intl.NumberFormat(iso639Code, {
    style: numberFormatStyle,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: currencyCode,
  })
    .format(amountWithDiscount)
    .replace(/\D00(?=\D*$)/, '');

  return money;
};
